(function () {
  "use strict";

  const API_BASE_URL = "https://calendar-service-cyan.vercel.app";

  class AppointmentCalendar {
    constructor(element) {
      this.element = element;

      this.appointmentType =
        element.dataset.appointmentType || "virtual";

      this.selectedDate = null;
      this.selectedTime = null;
      this.currentDate = new Date();

      this.cacheElements();
      this.bindEvents();
      this.render();
    }

    cacheElements() {
      this.calendarGrid =
        this.element.querySelector(
          "[data-calendar-grid]"
        );

      this.monthLabel =
        this.element.querySelector(
          "[data-calendar-month]"
        );

      this.previousButton =
        this.element.querySelector(
          "[data-calendar-prev]"
        );

      this.nextButton =
        this.element.querySelector(
          "[data-calendar-next]"
        );

      this.timesContainer =
        this.element.querySelector(
          "[data-calendar-times]"
        );

      this.selectedDateLabel =
        this.element.querySelector(
          "[data-selected-date]"
        );

      this.loading =
        this.element.querySelector(
          "[data-calendar-loading]"
        );

      this.error =
        this.element.querySelector(
          "[data-calendar-error]"
        );
    }

    bindEvents() {
      this.previousButton?.addEventListener(
        "click",
        () => {
          this.currentDate.setMonth(
            this.currentDate.getMonth() - 1
          );

          this.render();
        }
      );

      this.nextButton?.addEventListener(
        "click",
        () => {
          this.currentDate.setMonth(
            this.currentDate.getMonth() + 1
          );

          this.render();
        }
      );
    }

    render() {
      this.renderMonth();
      this.renderDays();
    }

    renderMonth() {
      const formatter =
        new Intl.DateTimeFormat(
          "en-GB",
          {
            month: "long",
            year: "numeric",
          }
        );

      this.monthLabel.textContent =
        formatter.format(this.currentDate);
    }

    renderDays() {
      this.calendarGrid.innerHTML = "";

      const year =
        this.currentDate.getFullYear();

      const month =
        this.currentDate.getMonth();

      const firstDay =
        new Date(year, month, 1);

      const lastDay =
        new Date(year, month + 1, 0);

      // Convert Sunday=0 to Monday=0
      const firstWeekday =
        (firstDay.getDay() + 6) % 7;

      for (
        let i = 0;
        i < firstWeekday;
        i++
      ) {
        const empty =
          document.createElement("div");

        empty.className =
          "appointment-calendar__day appointment-calendar__day--empty";

        this.calendarGrid.appendChild(empty);
      }

      const today = new Date();

      for (
        let day = 1;
        day <= lastDay.getDate();
        day++
      ) {
        const date =
          new Date(
            year,
            month,
            day
          );

        const button =
          document.createElement("button");

        button.type = "button";

        button.className =
          "appointment-calendar__day";

        button.textContent = day;

        const isPast =
          this.isBeforeToday(date);

        if (isPast) {
          button.disabled = true;

          button.classList.add(
            "appointment-calendar__day--disabled"
          );
        } else {
          button.addEventListener(
            "click",
            () => {
              this.selectDate(date);
            }
          );
        }

        if (
          date.toDateString() ===
          today.toDateString()
        ) {
          button.classList.add(
            "appointment-calendar__day--today"
          );
        }

        this.calendarGrid.appendChild(
          button
        );
      }
    }

    isBeforeToday(date) {
      const today = new Date();

      today.setHours(0, 0, 0, 0);

      return date < today;
    }

    formatDate(date) {
      const year =
        date.getFullYear();

      const month =
        String(
          date.getMonth() + 1
        ).padStart(2, "0");

      const day =
        String(
          date.getDate()
        ).padStart(2, "0");

      return `${year}-${month}-${day}`;
    }

    selectDate(date) {
      this.selectedDate = date;
      this.selectedTime = null;

      const formatted =
        this.formatDate(date);

      this.selectedDateLabel.textContent =
        date.toLocaleDateString(
          "en-GB",
          {
            day: "numeric",
            month: "long",
            year: "numeric",
          }
        );

      this.loadAvailability(
        formatted
      );
    }

    async loadAvailability(date) {
      this.showLoading();
      this.hideError();

      this.timesContainer.innerHTML = "";

      try {
        const url =
          `${API_BASE_URL}/api/availability` +
          `?appointmentType=${encodeURIComponent(
            this.appointmentType
          )}` +
          `&date=${encodeURIComponent(date)}`;

        const response =
          await fetch(url);

        const data =
          await response.json();

        if (!response.ok || !data.success) {
          throw new Error(
            data.message ||
              "Could not load availability."
          );
        }

        this.renderTimes(
          data.slots || []
        );
      } catch (error) {
        console.error(
          "Appointment availability error:",
          error
        );

        this.showError(
          "Sorry, we couldn't load the available times. Please try again."
        );
      } finally {
        this.hideLoading();
      }
    }

    renderTimes(slots) {
      this.timesContainer.innerHTML = "";

      if (!slots.length) {
        const empty =
          document.createElement("p");

        empty.className =
          "appointment-calendar__no-times";

        empty.textContent =
          "No appointments are available on this date.";

        this.timesContainer.appendChild(
          empty
        );

        return;
      }

      slots.forEach((slot) => {
        const button =
          document.createElement("button");

        button.type = "button";

        button.className =
          "appointment-calendar__time";

        button.textContent =
          this.formatTime(
            slot.start
          );

        button.addEventListener(
          "click",
          () => {
            this.selectTime(
              slot,
              button
            );
          }
        );

        this.timesContainer.appendChild(
          button
        );
      });
    }

    formatTime(isoString) {
      return new Intl.DateTimeFormat(
        "en-GB",
        {
          timeZone:
            "Europe/London",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }
      ).format(
        new Date(isoString)
      );
    }

    selectTime(slot, button) {
      this.selectedTime =
        slot.start;

      this.timesContainer
        .querySelectorAll(
          ".appointment-calendar__time"
        )
        .forEach((element) => {
          element.classList.remove(
            "is-selected"
          );
        });

      button.classList.add(
        "is-selected"
      );

      this.element.dispatchEvent(
        new CustomEvent(
          "appointment-time-selected",
          {
            bubbles: true,
            detail: {
              appointmentType:
                this.appointmentType,
              date:
                this.selectedDate,
              startTime:
                slot.start,
            },
          }
        )
      );
    }

    showLoading() {
      this.loading.hidden = false;
    }

    hideLoading() {
      this.loading.hidden = true;
    }

    showError(message) {
      this.error.textContent =
        message;

      this.error.hidden = false;
    }

    hideError() {
      this.error.hidden = true;
    }

    setAppointmentType(type) {
      if (
        ![
          "virtual",
          "atelier",
        ].includes(type)
      ) {
        console.warn(
          "Unsupported appointment type:",
          type
        );

        return;
      }

      this.appointmentType = type;

      this.selectedDate = null;
      this.selectedTime = null;

      this.timesContainer.innerHTML = "";

      this.selectedDateLabel.textContent =
        "Select a date";
    }
  }

  function initialiseCalendars() {
    document
      .querySelectorAll(
        "[data-appointment-calendar]"
      )
      .forEach((element) => {
        const calendar =
          new AppointmentCalendar(
            element
          );

        element.appointmentCalendar =
          calendar;
      });
  }

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      initialiseCalendars
    );
  } else {
    initialiseCalendars();
  }
})();